// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState } from "@bg/App/Store/Reducers";
import LazyImage, { LazyImageProps } from "@bg/MobiusOverrides/LazyImage";
import { checkoutPages } from "@bg/XDL/constants";
import { xClassNames, xScreen } from "@bg/XDL/Utilities";
import { useEagerLogoLoading } from "@insite/client-framework/Common/EagerLoadingLogo";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import setInitialValues from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetInitialValues";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getHomePageUrl } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import * as cssLinter from "css";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { Dispatch } from "redux";
import { css } from "styled-components";

const enum fields {
    logoImage = "logoImage",
    isHeaderLogo = "isHeaderLogo",
    logoImageSticky = "logoImageSticky",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.logoImage]: string;
        [fields.logoImageSticky]: string;
        [fields.isHeaderLogo]: boolean;
        [fields.customCSS]: string;
    };
    extendedStyles?: LogoStyles;
    externalCustomCSS?: string;
}

export interface LogoStyles {
    wrapper?: InjectableCss;
    linkWrapper?: InjectableCss;
    menu?: InjectableCss;
    menuTriggerButton?: ClickablePresentationProps;
    image?: LazyImageProps;
    stickyImage?: LazyImageProps;
    logoImage?: LazyImageProps;
    linkStyle?: LinkPresentationProps;
    logoLinkStyle?: LinkPresentationProps;
    menuIcon?: IconPresentationProps;
}

export const logoStyles: LogoStyles = {
    wrapper: {
        css: css`
            max-width: 100%;
            max-height: 100%;
            display: flex;
            img {
                width: 100%;
                height: 100%;
            }
        `,
    },
    logoImage: {
        css: css`
            &.checkout-logo {
                height: 48px;
            }
        `,
    },
    menu: {
        css: css`
            display: flex;
            align-items: center;
            margin-right: 39.7px;
            ${({ sticky }: any) =>
                !sticky &&
                css`
                    display: none;
                `}
            ${xScreen("lte", "md")} {
                display: flex;
                margin-right: 20px;
            }
            @media screen and (max-width: 359px) {
                margin-right: 10px;
            }
        `,
    },
    menuTriggerButton: {},
    image: {
        css: css`
            width: 100%;

            img {
                ${({ sticky }: any) => sticky && `display: none;`}
            }
        `,
    },
    stickyImage: {
        css: css`
            width: 100%;
            &img,
            & {
                max-width: 50px;
                max-height: 50px;
            }
        `,
    },
    linkStyle: {
        css: css`
            width: 100%;
            ${({ sticky }: any) => sticky && `height: 50px; >span {height: 50px;}`}
        `,
    },
    linkWrapper: {
        css: css``,
    },
};

const mapStateToProps = (state: CustomAppState) => ({
    isSticky: state.xHeader.isSticky,
    navDrawerIsOpen: state.components.addressDrawer.navDrawerIsOpen,
    homePageLink: getHomePageUrl(state),
    isCheckoutPage: checkoutPages.includes(getCurrentPage(state).type),
});

const mapDispatchToProps = {
    setInitialValues,
    setNavDrawerIsOpen,
};

type Props = OwnProps & WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const StyledWrapperX = getStyledWrapper("div") as any;

const Logo: React.FunctionComponent<Props> = ({
    fields,
    extendedStyles,
    isSticky,
    setNavDrawerIsOpen,
    setInitialValues,
    navDrawerIsOpen,
    homePageLink,
    externalCustomCSS,
    isCheckoutPage,
}: Props) => {
    const [styles] = React.useState(() => mergeToNew(logoStyles, extendedStyles));

    const [isSmallScreen, setIsSmallScreen] = React.useState(false);

    React.useEffect(() => {
        const updateSmallScreen = () => {
            setIsSmallScreen(window.matchMedia("(max-width: 1024px) and (hover: none)").matches);
        };
        updateSmallScreen();
        window.addEventListener("resize", updateSmallScreen, { passive: true });
        return () => {
            window.removeEventListener("resize", updateSmallScreen);
        };
    }, []);

    const openDrawer = () => {
        setNavDrawerIsOpen({ navDrawerIsOpen: true });
        setInitialValues({});
    };

    const closeDrawer = () => {
        setNavDrawerIsOpen({ navDrawerIsOpen: false });
        setInitialValues({});
    };
    const isEager = useEagerLogoLoading();

    const customCssWrapper = {
        css: css`
            ${externalCustomCSS || fields.customCSS}
        `,
    };

    if (fields.isHeaderLogo) {
        return (
            <StyledWrapper {...customCssWrapper}>
                <StyledWrapperX className="wrapper" {...styles.wrapper} sticky={isSticky}>
                    <StyledWrapperX sticky={isSticky} {...styles.menu}>
                        {navDrawerIsOpen ? (
                            <Clickable
                                onClick={closeDrawer}
                                {...logoStyles.menuTriggerButton}
                                data-test-selector="expandMobileMenu"
                            >
                                <Icon
                                    size={30}
                                    src="Clear"
                                    color="common.white"
                                    {...styles.menuIcon}
                                    key="close-menu-icon"
                                />
                                <VisuallyHidden>{translate("close menu")}</VisuallyHidden>
                            </Clickable>
                        ) : (
                            <Clickable
                                onClick={openDrawer}
                                {...logoStyles.menuTriggerButton}
                                data-test-selector="expandMobileMenu"
                            >
                                <Icon
                                    size={30}
                                    src="Menu"
                                    color="common.white"
                                    {...styles.menuIcon}
                                    key="open-menu-icon"
                                />
                                <VisuallyHidden>{translate("open menu")}</VisuallyHidden>
                            </Clickable>
                        )}
                    </StyledWrapperX>

                    <Link
                        href={homePageLink}
                        className="home-page-link"
                        {...styles.linkStyle}
                        {...({ sticky: isSticky } as any)}
                    >
                        {!isSticky || (isSticky && !fields.logoImageSticky) || isSmallScreen ? (
                            <LazyImage
                                disableLazyLoad={true}
                                src={
                                    fields.isMobileSpecific
                                        ? fields.mobileSpecificImage || fields.logoImage
                                        : fields.logoImage
                                }
                                altText={translate("home logo")}
                                {...styles.image}
                                className="img"
                            />
                        ) : (
                            <LazyImage
                                disableLazyLoad={true}
                                src={fields.logoImageSticky}
                                altText={translate("home logo")}
                                {...styles.stickyImage}
                                className="img"
                            />
                        )}
                    </Link>
                </StyledWrapperX>
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapper {...styles.linkWrapper}>
                <Link href={homePageLink} className="home-page-link" {...styles.logoLinkStyle}>
                    <LazyImage
                        src={fields.logoImage}
                        className={xClassNames.append(isCheckoutPage && "checkout-logo", "img")}
                        altText={translate("home")}
                        {...styles.logoImage}
                    />
                </Link>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const defaultCustomCss = `.wrapper {
}

.home-page-link {
}

.img {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(Logo),
    definition: {
        group: "XNGAGE" as any,
        icon: "diamond",
        fieldDefinitions: [
            {
                name: fields.logoImage,
                displayName: "Logo Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.isHeaderLogo,
                displayName: "Header Specific Image",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isRequired: true,
                variant: "toggle",
                tab: basicTab,
            },
            {
                name: fields.logoImageSticky,
                displayName: "Sticky Logo Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "Translatable",
                isVisible: item => item.fields.isHeaderLogo === true,
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
